import { useTranslation } from "react-i18next";
import { Certificate, Partition } from "src/types/osbuilder";

import { FileContentType } from "@dashboard/files/types";
import { ApplicationModule, Product } from "@dashboard/products/types";
import { ControlledSelectField } from "@shared/form/SelectField/SelectField";

import { ModuleFileDialog, ModuleFileFormValues } from "../ModuleFileDialog";
import SharedUtils from "../ModulePanel.utils";

import Utils from "./ApplicationDialog.utils";

type FormValues = ModuleFileFormValues & {
    partition: Partition;
    privileged: "regular" | "privileged";
    certificate: Certificate;
};

type Props = {
    product: Product | null;
    module: ApplicationModule | null;
    open: boolean;
    onToggle: (value: boolean) => void;
};

const contentTypes = [FileContentType.Apk, FileContentType.ApkV2];

export const ApplicationDialog = (props: Props) => {
    const { t } = useTranslation(["modals"]);

    return (
        <ModuleFileDialog<FormValues, ApplicationModule>
            product={props.product}
            module={props.module}
            open={props.open}
            onToggle={props.onToggle}
            title={t("modals:manageProductModules.application.title")}
            description={t("modals:manageProductModules.application.description")}
            noFilesAvailable={t("modals:manageProductModules.application.noFilesAvailable")}
            contentTypes={contentTypes}
            schema={Utils.schema}
            extraInitialValues={{
                partition: props.module?.partition ?? "product",
                privileged: props.module?.privileged ? "privileged" : "regular",
                certificate: props.module?.certificate ?? "presigned",
            }}
            newModuleDefaults={{ type: "prebuilt_app", tags: ["Own"] }}
            buildModuleValues={(file, sourceId, values) => ({
                name: file.filename,
                sourceId,
                partition: values.partition,
                privileged: values.privileged === "privileged",
                certificate: values.certificate,
            })}
        >
            <ControlledSelectField
                name="partition"
                label={t("modals:manageProductModules.application.input.partition.caption")}
                options={SharedUtils.partitionOptions}
            />
            <ControlledSelectField
                name="privileged"
                label={t("modals:manageProductModules.application.input.privileged.caption")}
                options={Utils.privilegedOptions}
            />
            <ControlledSelectField
                name="certificate"
                label={t("modals:manageProductModules.application.input.certificate.caption")}
                options={Utils.getCertificateOptions(props.module?.certificate)}
            />
        </ModuleFileDialog>
    );
};
